import { Box, HStack, IconButton, Text, useBreakpointValue } from "@chakra-ui/react";
import { MenuRoot, MenuTrigger } from "@chakra-ui/react";
import {
  MdArrowBack as BackIcon,
  MdMoreVert as MoreIcon,
  MdPlaylistAdd as AddRecipesIcon,
} from "react-icons/md";
import HeaderMenu from "@/components/HeaderMenu";
import CollectionActionsMenu from "@/components/CollectionActionsMenu";
import { getCollectionInitials, getCollectionColour } from "@/lib/collectionUtils";

interface CollectionHeroStripProps {
  id: string;
  title: string;
  recipeIds: string[];
  onBack?: () => void;
  onAddRecipes?: () => void;
  onRename?: () => void;
  onDelete?: () => void;
  deleteLoading?: boolean;
}

export default function CollectionHeroStrip({
  id,
  title,
  recipeIds,
  onBack,
  onAddRecipes,
  onRename,
  onDelete,
  deleteLoading,
}: CollectionHeroStripProps) {
  const isMobile = useBreakpointValue({ base: true, lg: false });
  const recipeCount = recipeIds.length;
  const initials = getCollectionInitials(title);
  const bg = getCollectionColour(id);

  return (
    <Box position="relative" h={{ base: "240px", md: "260px" }} overflow="hidden" style={{ background: bg }}>
      {/* Initials watermark */}
      <Box
        position="absolute"
        inset={0}
        display="flex"
        alignItems="center"
        justifyContent="center"
        pointerEvents="none"
        aria-hidden
      >
        <Text
          fontFamily="heading"
          fontWeight="800"
          fontSize="8xl"
          color="white"
          userSelect="none"
          style={{ opacity: 0.12 }}
        >
          {initials}
        </Text>
      </Box>

      {/* Gradient — darkens top (icons) and bottom (title) */}
      <Box
        position="absolute"
        inset={0}
        style={{
          background:
            "linear-gradient(to bottom, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.05) 35%, transparent 50%, rgba(0,0,0,0.5) 70%, rgba(0,0,0,0.80) 100%)",
        }}
        pointerEvents="none"
      />

      {/* Floating nav row — back left, actions right (mobile only) */}
      {isMobile && (
        <HStack
          position="absolute"
          top={0} left={0} right={0}
          px={3}
          pt="calc(env(safe-area-inset-top, 0px) + 12px)"
          justify="space-between"
          align="center"
          zIndex={10}
        >
          {onBack && (
            <IconButton
              aria-label="Go back"
              variant="ghost"
              size="md"
              borderRadius="full"
              color="white"
              _hover={{ bg: "rgba(255,255,255,0.15)" }}
              onClick={onBack}
            >
              <BackIcon size={24} />
            </IconButton>
          )}

          <HStack gap={2} ml="auto">
            {onAddRecipes && (
              <IconButton
                aria-label="Add recipes"
                variant="ghost"
                size="md"
                borderRadius="full"
                color="white"
                _hover={{ bg: "rgba(255,255,255,0.15)" }}
                onClick={onAddRecipes}
              >
                <AddRecipesIcon size={24} />
              </IconButton>
            )}

            {onDelete && (
              <MenuRoot>
                <MenuTrigger asChild>
                  <IconButton
                    aria-label="Collection actions"
                    variant="ghost"
                    size="md"
                    borderRadius="full"
                    color="white"
                    _hover={{ bg: "rgba(255,255,255,0.15)" }}
                  >
                    <MoreIcon size={24} />
                  </IconButton>
                </MenuTrigger>
                <HeaderMenu>
                  <CollectionActionsMenu
                    onRename={onRename}
                    onDelete={onDelete}
                    deleteLoading={deleteLoading}
                  />
                </HeaderMenu>
              </MenuRoot>
            )}
          </HStack>
        </HStack>
      )}

      {/* Title + recipe count at bottom */}
      <Box position="absolute" bottom={0} left={0} right={0} px={4} pb={4}>
        <HStack align="center" gap={2}>
          <Text
            fontFamily="heading"
            fontWeight="800"
            fontSize={{ base: "2xl", md: "3xl" }}
            color="white"
            lineHeight={1.1}
            style={{ textShadow: "0 1px 4px rgba(0,0,0,0.4)" }}
          >
            {title}
          </Text>
          <Box
            fontSize="11px"
            fontWeight="500"
            fontFamily="body"
            px={3}
            py="3px"
            borderRadius="full"
            border="1px solid"
            borderColor="rgba(255,255,255,0.35)"
            color="white"
            flexShrink={0}
            style={{ backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)", background: "rgba(0,0,0,0.32)" }}
          >
            {recipeCount} {recipeCount === 1 ? "recipe" : "recipes"}
          </Box>
        </HStack>
      </Box>
    </Box>
  );
}
